<script setup>
import { computed, ref } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import Popover from 'primevue/popover'
import { getInitials } from '../../../../../helpers.js'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'

const props = defineProps({
    reservation: { type: Object, default: null },
})

const emit = defineEmits(['close', 'statusChanged'])

const page = usePage()

// ─── Derived ─────────────────────────────────────────────────────

const form     = computed(() => props.reservation?.reservation_forms ?? null)
const formColor = computed(() => getFormColor(form.value))
const worker    = computed(() => props.reservation?.workers ?? null)

const statusLabel = computed(() => {
    const map = {
        accepted: trans('reservations.status_accepted'),
        pending:  trans('reservations.status_pending'),
        rejected: trans('reservations.status_rejected'),
    }
    return map[props.reservation?.state] ?? ''
})

const dateLabel = computed(() => {
    const r = props.reservation
    if (!r) return ''
    if (r.time_start) {
        return `${r.time_start.substring(0, 5)} – ${r.time_end?.substring(0, 5) ?? ''}`
    }
    return null
})

const dateRange = computed(() => {
    const r = props.reservation
    if (!r) return null
    if (r.date_start && r.date_end) {
        const days = Math.round((new Date(r.date_end) - new Date(r.date_start)) / 86400000) + 1
        return { from: r.formatted_date, to: r.formatted_date_end, days }
    }
    return null
})

const locale = computed(() => (page.props.locale ?? 'cs').replace('_', '-'))

const totalPrice = computed(() => {
    const items = props.reservation?.bill_items
    if (!items?.length) return null
    const sum = items.reduce((s, i) => s + i.unit_price_with_vat * i.amount, 0)
    return new Intl.NumberFormat(locale.value, { style: 'currency', currency: 'CZK', minimumFractionDigits: 0 }).format(sum)
})

const formData = computed(() => {
    const raw = props.reservation?.form_data
    if (!raw) return null
    try {
        const fd = JSON.parse(raw)
        if (Array.isArray(fd) || !fd) return null
        return Object.entries(fd)
    } catch { return null }
})

// ─── Akce ────────────────────────────────────────────────────────

const isLoading = ref(false)

function accept() {
    isLoading.value = true
    router.get(`/app/forms/event/${props.reservation.id}/accept`, {}, {
        preserveScroll: true,
        onFinish: () => { isLoading.value = false; emit('statusChanged') },
    })
}

function reject() {
    isLoading.value = true
    router.get(`/app/forms/event/${props.reservation.id}/reject`, {}, {
        preserveScroll: true,
        onFinish: () => { isLoading.value = false; emit('statusChanged') },
    })
}

// Odeslat notifikaci
const notifyPopover = ref()
const notifyOptions = [
    { type: 'new_client',     label: trans('reservations.notify_new_client') },
    { type: 'confirm_client', label: trans('reservations.notify_confirm') },
    { type: 'cancel_client',  label: trans('reservations.notify_cancel') },
    { type: 'reminder_client',label: trans('reservations.notify_reminder') },
]
function sendNotification(type) {
    router.post(`/app/reservations/${props.reservation.id}/notify`, { type }, {
        preserveState: true,
        onSuccess: () => notifyPopover.value?.hide(),
    })
}

// ─── Activity log ────────────────────────────────────────────────

const LOG_MAP = {
    new_reservation:                          { labelKey: 'reservations.activity_new',              origin: 'client' },
    confirm_reservation:                      { labelKey: 'reservations.activity_confirm',           origin: 'user' },
    cancel_reservation:                       { labelKey: 'reservations.activity_cancel',            origin: 'ambiguous' },
    change_of_date:                           { labelKey: 'reservations.activity_date_change',       origin: 'ambiguous' },
    change_of_date_of_time_start_of_time_end: { labelKey: 'reservations.activity_datetime_change',  origin: 'ambiguous' },
    change_of_time_start_of_time_end:         { labelKey: 'reservations.activity_time_change',      origin: 'ambiguous' },
    change_of_time_start:                     { labelKey: 'reservations.activity_time_start_change', origin: 'ambiguous' },
    change_of_time_end:                       { labelKey: 'reservations.activity_time_end_change',  origin: 'ambiguous' },
    change_reservation_email:                 { labelKey: 'reservations.activity_email_change',     origin: 'system' },
    confirm_reservation_email:                { labelKey: 'reservations.activity_email_confirm',    origin: 'system' },
    new_reservation_email:                    { labelKey: 'reservations.activity_email_new',        origin: 'system' },
    cancel_reservation_email:                 { labelKey: 'reservations.activity_email_cancel',     origin: 'system' },
    change_reservation_sms:                   { labelKey: 'reservations.activity_sms_change',       origin: 'system' },
    confirm_reservation_sms:                  { labelKey: 'reservations.activity_sms_confirm',      origin: 'system' },
    new_reservation_sms:                      { labelKey: 'reservations.activity_sms_new',          origin: 'system' },
    cancel_reservation_sms:                   { labelKey: 'reservations.activity_sms_cancel',       origin: 'system' },
    pending_state_changed:                    { labelKey: 'reservations.activity_state_pending',    origin: 'user' },
    accepted_state_changed:                   { labelKey: 'reservations.activity_state_accepted',   origin: 'user' },
    rejected_state_changed:                   { labelKey: 'reservations.activity_state_rejected',   origin: 'user' },
}

function logOriginName(origin) {
    const r = props.reservation
    if (!r) return ''
    if (origin === 'client')    return r.name
    if (origin === 'system')    return trans('common.app_name')
    if (origin === 'user')      return page.props.user?.name ?? trans('common.admin_label')
    return (page.props.user?.name ?? trans('common.admin_label')) + ' ' + trans('common.or') + ' ' + r.name
}

const activityLogs = computed(() =>
    (props.reservation?.activity_logs ?? []).map(log => {
        const def = LOG_MAP[log.action] ?? { labelKey: null, origin: 'ambiguous' }
        return {
            label:  def.labelKey ? trans(def.labelKey) : log.action,
            origin: logOriginName(def.origin),
            date:   log.date_friendly,
            isSystem: def.origin === 'system',
        }
    })
)
</script>

<template>
    <Teleport to="body">
        <!-- Scrim -->
        <Transition name="scrim">
            <div
                v-if="reservation"
                class="fixed inset-0 bg-black/25 backdrop-blur-[2px] z-[100]"
                @click="emit('close')"
            />
        </Transition>

        <!-- Drawer panel -->
        <Transition name="drawer">
            <div
                v-if="reservation"
                class="fixed top-0 right-0 bottom-0 w-[440px] max-w-full bg-white dark:bg-neutral-900 z-[101] flex flex-col shadow-[_-20px_0_50px_-10px_rgba(15,17,21,.22)]"
            >
                <!-- Header -->
                <div
                    class="flex items-center justify-between px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 flex-shrink-0"
                    :style="{ borderTop: `4px solid ${formColor}` }"
                >
                    <div
                        class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold"
                        :style="{ background: formColor + '1A', color: formColor }"
                    >
                        <span class="w-2 h-2 rounded-full flex-shrink-0" :style="{ background: formColor }"/>
                        {{ form?.name }}
                    </div>
                    <button
                        class="w-9 h-9 rounded-full flex items-center justify-center text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
                        @click="emit('close')"
                    >
                        <i class="pi pi-times !text-sm"/>
                    </button>
                </div>

                <!-- Body -->
                <div class="flex-1 overflow-y-auto px-6 py-5">
                    <!-- Status -->
                    <div
                        class="text-[11px] font-semibold   mb-1.5"
                        :class="{
                            'text-emerald-600': reservation.state === 'accepted',
                            'text-amber-600':   reservation.state === 'pending',
                            'text-red-500':     reservation.state === 'rejected',
                        }"
                    >
                        ●&nbsp;{{ statusLabel }}
                    </div>

                    <!-- Title -->
                    <h2 class="text-[22px] font-semibold leading-tight tracking-tight text-neutral-900 dark:text-white mb-6">
                        {{ reservation.name }}
                    </h2>

                    <!-- Meta -->
                    <div class="flex flex-col gap-4 pb-6 border-b border-neutral-100 dark:border-neutral-800">

                        <!-- Termín -->
                        <div class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_date') }}</div>
                            <div class="meta-val">
                                <template v-if="dateLabel">
                                    {{ reservation.formatted_date }}<br/>
                                    <b>{{ dateLabel }}</b>
                                </template>
                                <template v-else-if="dateRange">
                                    <b>{{ dateRange.from }} – {{ dateRange.to }}</b><br/>
                                    <span class="text-neutral-400 text-[12px]">{{ dateRange.days }} {{ $t('common.days') }}</span>
                                </template>
                                <template v-else>
                                    {{ reservation.formatted_date }}
                                </template>
                            </div>
                        </div>

                        <!-- Klient -->
                        <div class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_client') }}</div>
                            <div class="meta-val">
                                <b>{{ reservation.name }}</b>
                                <div v-if="reservation.email" class="text-neutral-400 text-[12px] mt-0.5">
                                    {{ reservation.email }}
                                </div>
                                <div v-if="reservation.phone" class="text-neutral-400 text-[12px]">
                                    {{ reservation.phone }}
                                </div>
                            </div>
                        </div>

                        <!-- Kolega -->
                        <div v-if="worker" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_worker') }}</div>
                            <div class="meta-val flex items-center gap-2">
                                <div class="w-6 h-6 rounded-full bg-neutral-700 text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                                    {{ getInitials(worker.name) }}
                                </div>
                                <b>{{ worker.name }}</b>
                            </div>
                        </div>

                        <!-- Služba -->
                        <div v-if="reservation.services?.name" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_service') }}</div>
                            <div class="meta-val">{{ reservation.services.name }}</div>
                        </div>

                        <!-- Předmět (rental) -->
                        <div v-if="reservation.rental_items?.name" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_item') }}</div>
                            <div class="meta-val">{{ reservation.rental_items.name }}</div>
                        </div>

                        <!-- Hosté -->
                        <div v-if="reservation.adults != null" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_guests') }}</div>
                            <div class="meta-val">
                                {{ reservation.adults }} {{ $t('common.adults') }}
                                <template v-if="reservation.children"> · {{ reservation.children }} {{ $t('common.children') }}</template>
                                <template v-if="reservation.pets"> · {{ reservation.pets }} {{ $t('common.pets') }}</template>
                            </div>
                        </div>

                        <!-- Cena -->
                        <div v-if="totalPrice" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_price') }}</div>
                            <div class="meta-val">
                                <span class="text-[18px] font-semibold text-neutral-900">{{ totalPrice }}</span>
                            </div>
                        </div>

                        <!-- Zpráva -->
                        <div v-if="reservation.message" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_message') }}</div>
                            <div class="meta-val text-neutral-600">{{ reservation.message }}</div>
                        </div>

                        <!-- Form data -->
                        <div v-if="formData?.length" class="meta-row">
                            <div class="meta-lbl">{{ $t('reservations.meta_form') }}</div>
                            <div class="meta-val flex flex-col gap-0.5 text-[12px]">
                                <div v-for="[key, val] in formData" :key="key">
                                    <span class="text-neutral-400">{{ key }}:</span> {{ val }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Akce pro čekající -->
                    <div v-if="reservation.state === 'pending'" class="py-5 border-b border-neutral-100 dark:border-neutral-800">
                        <div class="text-[11px] font-semibold   text-neutral-400 dark:text-neutral-500 mb-3">{{ $t('reservations.section_actions') }}</div>
                        <div class="flex gap-2">
                            <button
                                class="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600 hover:text-neutral-900 dark:hover:text-white bg-white dark:bg-neutral-800 disabled:opacity-50 transition-colors"
                                :disabled="isLoading"
                                @click="accept"
                            >
                                <i class="pi pi-check !text-sm text-emerald-600"/>
                                {{ $t('reservations.btn_confirm') }}
                            </button>
                            <button
                                class="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600 hover:text-neutral-900 dark:hover:text-white bg-white dark:bg-neutral-800 disabled:opacity-50 transition-colors"
                                :disabled="isLoading"
                                @click="reject"
                            >
                                <i class="pi pi-times !text-sm text-red-500"/>
                                {{ $t('reservations.btn_reject') }}
                            </button>
                        </div>
                    </div>

                    <!-- Historie -->
                    <div v-if="activityLogs.length" class="pt-5">
                        <div class="text-[11px] font-semibold   text-neutral-400 dark:text-neutral-500 mb-3">{{ $t('reservations.section_history') }}</div>
                        <div class="flex flex-col">
                            <div
                                v-for="(log, i) in activityLogs"
                                :key="i"
                                class="flex gap-3 py-2.5"
                            >
                                <div class="flex flex-col items-center gap-0 flex-shrink-0 mt-1">
                                    <div
                                        class="w-2 h-2 rounded-full flex-shrink-0"
                                        :class="log.isSystem ? 'bg-neutral-300 dark:bg-neutral-600' : 'bg-blue-500'"
                                    />
                                    <div v-if="i < activityLogs.length - 1" class="w-px flex-1 bg-neutral-100 dark:bg-neutral-800 mt-1"/>
                                </div>
                                <div class="text-sm pb-1">
                                    <span class="font-semibold text-neutral-800 dark:text-neutral-100 mr-1">{{ log.origin }}</span>
                                    <span class="text-neutral-600 dark:text-neutral-400"> {{ log.label }}</span>
                                    <div class="text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5">{{ log.date }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="flex items-center gap-2 px-5 py-3.5 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-800/50 flex-shrink-0">

                    <div class="flex-1"/>

                    <button
                        class="px-3 py-1.5 rounded-lg text-sm font-medium border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600 hover:text-neutral-800 dark:hover:text-white bg-white dark:bg-neutral-800 transition-colors"
                        @click="emit('close')"
                    >
                        {{ $t('reservations.btn_close') }}
                    </button>
                    <Link
                        :href="`/app/reservations/${reservation.sqid}`"
                        class="px-3 py-1.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        {{ $t('reservations.btn_edit') }}
                    </Link>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
/* Scrim transition */
.scrim-enter-active, .scrim-leave-active { transition: opacity .2s ease; }
.scrim-enter-from, .scrim-leave-to       { opacity: 0; }

/* Drawer slide transition */
.drawer-enter-active { transition: transform .28s cubic-bezier(.25,1,.3,1); }
.drawer-leave-active { transition: transform .22s cubic-bezier(.4,0,1,1); }
.drawer-enter-from, .drawer-leave-to { transform: translateX(100%); }

/* Meta grid */
.meta-row {
    display: grid;
    grid-template-columns: 90px 1fr;
    gap: 14px;
    align-items: start;
    font-size: 13px;
}

.meta-lbl {
    font-size: 11px;
    font-weight: 600;
    text-transform: ;
    letter-spacing: .04em;
    color: #9BA1AB;
    padding-top: 2px;
}

.meta-val {
    color: #0F1115;
    line-height: 1.5;
}

:root.dark .meta-lbl {
    color: #6b737e;
}

:root.dark .meta-val {
    color: #f0f0f2;
}
</style>
